import React, { useCallback, useEffect, useState } from 'react'
import zionDebug from '@fs/zion-debug'
import { useFeatureFlag } from '@fs/zion-flags'
import { useParams, useQueryParams } from '@fs/zion-router'
import { getTimestamp, publicAccountScreens, replacePersonInRelationships } from '../../lib/helpers'
import { getPersonsPortraits, getReservedNames, setWorkingPersonData } from '../../lib/services'
import { personMunge } from '../../lib/mungers'
import useNavigate from '../../hooks/useNavigate'
import useCompareUuids from '../../hooks/useCompareUuids'
import PageSpinner from '../common/PageSpinner'

const debug = zionDebug('FirstTimeTempleExperience:FtteProvider')

const handleReservations = (preparedPersons, dispatch, memberUuid) => {
  const mungedReservedPersons = preparedPersons?.map(personMunge) ?? []
  dispatch({ type: 'SET_PREPARED_PERSONS', preparedPersons: mungedReservedPersons, memberUuid })
  return mungedReservedPersons
}

const handleReservedNamesError = (error, dispatch) => {
  debug(`The following error occurred while reading the reserved names: ${JSON.stringify(error?.response)}`)
  dispatch({
    type: 'SET_ERROR_TYPE',
    errorType: error?.response?.status >= 500 ? 'SYSTEM_DOWN' : 'READ_RESERVED_NAMES',
  })
}

function fetchReservedNames(dispatch, memberUuid, isTryDemo) {
  const { cachePromise, serverPromise } = getReservedNames(memberUuid, isTryDemo)
  return {
    cachePromise: cachePromise
      .then((preparedPersons) => handleReservations(preparedPersons, dispatch, memberUuid))
      .catch((error) => handleReservedNamesError(error, dispatch)),
    serverPromise: serverPromise
      .then((preparedPersons) => handleReservations(preparedPersons, dispatch, memberUuid))
      .catch((error) => handleReservedNamesError(error, dispatch)),
  }
}

function getRedirectUrl(url, location) {
  const { search } = location
  if (!search) return [url]
  return [url, null, true, false, search]
}

/**
 * @describe - Handles redirect logic in add-name and account pages/steps.
 */
export default function FtteRedirectLayer({ children, isAccountAssociatedWithUuid, providerValues }) {
  const [isRedirecting, setIsRedirecting] = useState(null)
  const { step } = useParams()
  const { query } = useQueryParams()
  const { memberUuid, preventProdRedirect } = query ?? {}
  const navigate = useNavigate()
  const { isTryDemo } = navigate
  const { state, dispatch } = providerValues
  const { coupleRelationships, metadata, parentChildRelationships, persons } = state ?? {}
  const { lastCompletedStep, potentialMatches } = metadata ?? {}

  // `preventProdRedirect` is used (and should only be used) by the QA Performance team for the PageStats reports
  const disableRedirect = useFeatureFlag('FTTE_FTTE_DisableRedirect').isOn || preventProdRedirect === 'true'
  const isSameAccount = useCompareUuids(dispatch)

  const redirectToAlreadyAssigned = useCallback(() => {
    const params = getRedirectUrl('/account/already-assigned', navigate.location)
    navigate.onReplace(...params)

    setIsRedirecting(false)
  }, [navigate])

  const redirectToAccountSetup = useCallback(() => {
    // Navigate the user to the account setup steps if there is a UUID in the URL and the member's account is not yet created with their MRN
    // Don't navigate user away from /review -- this is most likely the use case where the member logs in on the bishop's device.
    const params = getRedirectUrl('/account', navigate.location)
    navigate.onReplace(...params)

    setIsRedirecting(false)
  }, [navigate])

  const redirectToLastStep = useCallback(() => {
    let redirectToUrl = null

    if (lastCompletedStep === 'relation') redirectToUrl = '/add-name/relation'
    else if (lastCompletedStep === 'tree-list') redirectToUrl = '/add-name/tree-list'
    else if (lastCompletedStep === 'name') redirectToUrl = '/add-name/name'
    else if (lastCompletedStep === 'vitals') redirectToUrl = '/add-name/vitals'
    else if (lastCompletedStep === 'confirm') redirectToUrl = '/add-name/confirm'
    else if (lastCompletedStep === 'father') redirectToUrl = '/add-name/father'
    else if (lastCompletedStep === 'mother') redirectToUrl = '/add-name/mother'
    else if (lastCompletedStep === 'spouse') redirectToUrl = '/add-name/spouse'

    const params = getRedirectUrl(redirectToUrl, navigate.location)
    if (redirectToUrl) navigate.onReplace(...params)

    setIsRedirecting(false)
  }, [lastCompletedStep, navigate])

  const redirectToLandingPage = useCallback(() => {
    const MAX_WAIT_FOR_RESERVATION_LIST = 1500
    const startTimestamp = getTimestamp()
    const { cachePromise, serverPromise } = fetchReservedNames(dispatch, memberUuid, isTryDemo)

    // NOTE: We are making the assumption that is the user has names cached, then the service call will also return a list of reserved names
    // The only use case this would not be true is if the user just went to the temple and came back to our experience.
    cachePromise.then((preparedPersons) => {
      // Do not redirect user if it took too long
      const endTimestamp = getTimestamp()
      if (!preparedPersons || endTimestamp - startTimestamp > MAX_WAIT_FOR_RESERVATION_LIST) return

      // For the cached reserved names list, only redirect user if a list was present -- do not redirect user to /add-name if the list is empty here until serverPromise is resolved
      if (preparedPersons?.length) {
        const paramsFromReservedNames = getRedirectUrl('/', navigate.location)
        navigate.onReplace(...paramsFromReservedNames)
      }
    })

    serverPromise
      .then((preparedPersons) => {
        // Do not redirect user if it took too long
        const endTimestamp = getTimestamp()
        if (!preparedPersons || endTimestamp - startTimestamp > MAX_WAIT_FOR_RESERVATION_LIST) return null

        if (preparedPersons?.length) {
          const paramsFromReservedNames = getRedirectUrl('/', navigate.location)
          navigate.onReplace(...paramsFromReservedNames)
        } else {
          const paramsFromReservedNames = getRedirectUrl('/add-name', navigate.location)
          navigate.onReplace(...paramsFromReservedNames)
        }

        return preparedPersons
      })
      .finally((preparedPeople) => {
        setIsRedirecting(false)
        return preparedPeople
      })
      .then((preparedPeople) => {
        // Do nothing if there are no preparedPeople
        if (!preparedPeople?.length) return

        // NOTE: the endpoint only support upto 20 pids at a time.
        // Batch the pids into groups of 20 or less
        const MAX_PORTRAIT_PIDS = 20
        const portraitPids = preparedPeople.map((person) => person.id)
        while (portraitPids.length > 0) {
          const batchedPortraitPids = portraitPids.splice(0, MAX_PORTRAIT_PIDS)
          getPersonsPortraits(memberUuid, batchedPortraitPids, isTryDemo).then((newPortraits) => {
            dispatch({ type: 'SET_PORTRAITS', portraits: newPortraits })
          })
        }
      })
  }, [dispatch, isTryDemo, memberUuid, navigate])

  // Redirect User after all data is fetched
  useEffect(() => {
    // Determine whether current step should not be redirected
    const isNonRedirectableStep = ['names-ready', 'print', 'help', 'review'].includes(step)
    if (isNonRedirectableStep || disableRedirect) setIsRedirecting(false)
    else if (isRedirecting === null) {
      // In case the user refreshed, clicked browser's back button, or otherwise navigate back after selecting a match,
      // We need to reverse that by setting the workingPerson as the "selected" person again.
      const selectedMatch = potentialMatches?.find((potentialMatch) => potentialMatch.selected)
      if (selectedMatch) {
        // Empty out `potentialMatches` and move manuallyEnteredPerson back into persons
        const newMetadata = structuredClone(metadata)
        newMetadata.potentialMatches = []
        const newPersons = structuredClone(persons)
        persons.unshift(newMetadata.manuallyEnteredPerson)
        newMetadata.manuallyEnteredPerson = null

        // Revert relationships back to using the manually entered data
        const { newCoupleRelationships, newParentChildRelationships } = replacePersonInRelationships(
          coupleRelationships,
          parentChildRelationships,
          selectedMatch?.id,
          newPersons[0]?.id
        )

        // Update local state
        dispatch({
          type: 'SET_WORKING_PERSON_CONTAINER',
          metadata: newMetadata,
          persons: newPersons,
          coupleRelationships: newCoupleRelationships,
          parentChildRelationships: newParentChildRelationships,
        })

        // Update state on the backend
        setWorkingPersonData(
          memberUuid,
          newMetadata,
          newPersons,
          newCoupleRelationships,
          newParentChildRelationships,
          isTryDemo
        ).catch((error) => {
          debug(`The following error occurred while saving the tree data ${JSON.stringify(error?.response)}`)
        })
      }

      // Redirect to appropriate step/page
      try {
        if (isSameAccount !== undefined) {
          setIsRedirecting(true)
          const isPublicAccountStep = publicAccountScreens.includes(navigate.location.pathname)
          if (!isSameAccount) redirectToAlreadyAssigned()
          else if (!isAccountAssociatedWithUuid && !isPublicAccountStep) redirectToAccountSetup()
          else if (lastCompletedStep) redirectToLastStep()
          else if (!isPublicAccountStep) redirectToLandingPage()
        }
      } catch (error) {
        // Should anything fail, we don't want to leave the user in an eternal spinner state.
        // This will leave them on their current URL, potentially without redirecting them.
        setIsRedirecting(false)

        debug(
          `The following error occurred while retreiving the member's info and tree data in the first time temple experience: ${JSON.stringify(
            error?.response
          )}`
        )
        dispatch({
          type: 'SET_ERROR_TYPE',
          errorType: 'GENERAL_SYSTEM_ERROR',
          errorCode: error?.response?.status,
        })
      }
    }
  }, [
    coupleRelationships,
    disableRedirect,
    dispatch,
    isAccountAssociatedWithUuid,
    isRedirecting,
    isSameAccount,
    isTryDemo,
    lastCompletedStep,
    memberUuid,
    metadata,
    navigate.location.pathname,
    parentChildRelationships,
    persons,
    potentialMatches,
    redirectToAccountSetup,
    redirectToAlreadyAssigned,
    redirectToLandingPage,
    redirectToLastStep,
    step,
  ])

  if (disableRedirect) return children
  if (isRedirecting) return <PageSpinner data-testid="ftte-redirect-layer.spinner" />
  return children
}
